import { useState } from 'react';

import { Button, Center, Flex, Spinner, Stack, Text, useDisclosure } from '@chakra-ui/react';

import { PaginationState, SortingState } from '@tanstack/react-table';

import { useQueryClient } from '@tanstack/react-query';

import { AddCircleOutline } from '@aslanpay/portal-common-lib';

import { Table } from 'app/components/UI/Table/Table';
import RewardPointsModal from 'app/components/Employees/RewardPointsModal';

import { defaultPageSize } from 'app/constants/table';

import useFetchPots from 'app/hooks/useFetchPots';
import useFetchPointsTransactions from 'app/hooks/useFetchPointsTransactions';

import getPointsTransactionsColumns from 'app/utils/table/pointsTransactions';

import { IndividualPointsTabProps } from './types';

function IndividualPointsTab({ individual }: IndividualPointsTabProps) {
  const queryClient = useQueryClient();

  const {
    isOpen: isRewardPointsOpen,
    onOpen: onOpenRewardPoints,
    onClose: onCloseRewardPoints,
  } = useDisclosure();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const { data: pots, isLoading: isLoadingPots } = useFetchPots({
    relatedType: ['EMPLOYEE'],
    relatedIds: [individual?.id || ''],
    potType: ['PRIMARY_POINTS_POT'],
    page: 0,
    size: 1,
  });

  const pot = pots?.list.find((candidate) => candidate.potType === 'PRIMARY_POINTS_POT');

  const { data: pointsTransactions, isLoading: isLoadingPointsTransactions } =
    useFetchPointsTransactions({
      individualIds: [individual?.id || ''],
      sortBy: sorting[0]?.id,
      sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
      page: pageIndex,
      size: pageSize,
    });

  const handleRewardPoints = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
    queryClient.invalidateQueries({ queryKey: ['fetchPointsTransactions'] });
  };

  return (
    <Flex flexDir="column" w="full" gap="20px">
      {isLoadingPots || isLoadingPointsTransactions ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Flex
            w="100%"
            px="20px"
            py="10px"
            gap="20px"
            border="1px solid"
            borderRadius="10px"
            borderColor="brand.lightGray1"
            justify="space-between"
            align="center"
          >
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Points balance
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {pot?.balance}
              </Text>
            </Stack>
            {!individual?.onUnpaidLeave && (
              <Button
                leftIcon={<AddCircleOutline boxSize="24px" />}
                size="md"
                onClick={onOpenRewardPoints}
                isDisabled={individual?.status === 'DELETED' || individual?.status === 'LEFT'}
              >
                Reward points
              </Button>
            )}
          </Flex>

          <Table
            columns={getPointsTransactionsColumns()}
            data={pointsTransactions?.list || []}
            total={pointsTransactions?.total || 0}
            pageNumber={pointsTransactions?.pageNumber || pageIndex}
            pageSize={pointsTransactions?.pageSize || pageSize}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </>
      )}
      <RewardPointsModal
        id={individual?.id || ''}
        isOpen={isRewardPointsOpen}
        onClose={onCloseRewardPoints}
        onRewardPoints={handleRewardPoints}
      />
    </Flex>
  );
}

export default IndividualPointsTab;
